import axios from 'axios';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';

const BASE_URL = NAMESPACES.management_api.local_site_configuration;

const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// —— Payload types —— //

export interface PromotionPayload {
  promotion_name: string;
  promotion_description: string;
  promotion_url?: string;
  promotion_image?: string;
}

export interface UpdateItem {
  path: string[];
  value: any;
}
export interface UpdatePayload {
  updates: UpdateItem[];
}

export interface CoordinatesPayload {
  latitude: number;
  longitude: number;
}

// —— CRUD & common operations —— //

// Fetch (or auto‑init) the config
export const getLocalSiteConfig = (localId: number) =>
  retryWithBackoff(() => api.get(`/GetLocalSiteConfigurationByLocalId/${localId}`));

// Explicitly initialize
export const initLocalSiteConfig = (localId: number) =>
  retryWithBackoff(() => api.post(`/InitLocalSiteConfiguration/${localId}`));

// Generic granular update
export const updateLocalSiteConfig = (localId: number, payload: UpdatePayload) =>
  retryWithBackoff(() => api.put(`/UpdateLocalSiteConfiguration/${localId}`, payload));

// Delete entire config
export const deleteLocalSiteConfig = (localId: number) =>
  retryWithBackoff(() => api.delete(`/DeleteLocalSiteConfiguration/${localId}`));

// —— Single‑field updaters —— //

export const updateSiteUrl = (localId: number, url: string | null) =>
  retryWithBackoff(() => api.put(`/UpdateSiteUrl/${localId}`, { site_url: url }));

export const toggleShowSite = (localId: number) =>
  retryWithBackoff(() => api.post(`/ToggleShowSite/${localId}`));

export const toggleShowReservations = (localId: number) =>
  retryWithBackoff(() => api.post(`/ToggleShowReservations/${localId}`));

export const toggleShowPromotions = (localId: number) =>
  retryWithBackoff(() => api.post(`/ToggleShowPromotions/${localId}`));

export const toggleShowMenu = (localId: number) =>
  retryWithBackoff(() => api.post(`/ToggleShowMenu/${localId}`));

export const toggleShowPdfMenu = (localId: number) =>
  retryWithBackoff(() => api.post(`/ToggleShowPdfMenu/${localId}`));

// —— Phone endpoints —— //

export const updateSitePhone = (localId: number, phone: string | null) =>
  retryWithBackoff(() => api.put(`/UpdateSitePhone/${localId}`, { site_phone: phone }));

export const toggleShowPhone = (localId: number) =>
  retryWithBackoff(() => api.post(`/ToggleShowPhone/${localId}`));

export const toggleDeleteDescription = (localId: number) =>
  retryWithBackoff(() => api.post(`/ToggleDeleteDescription/${localId}`));

// —— Coordinates endpoints —— //

export const updateCoordinates = (localId: number, coords: CoordinatesPayload, place_id: string | null) =>
  retryWithBackoff(() => api.put(`/UpdateCoordinates/${localId}`, 
    {
      latitude: coords.latitude,
      longitude: coords.longitude,
      place_id: place_id,
    }));

export const toggleShowCoordinates = (localId: number) =>
  retryWithBackoff(() => api.post(`/ToggleShowCoordinates/${localId}`));

// —— Menu exclusions —— //

export const addMenuExclusionItem = (localId: number, itemId: number) =>
  retryWithBackoff(() => api.post(`/AddMenuExclusionItem/${localId}/${itemId}`));

export const addMenuExclusionOption = (localId: number, optionId: number) =>
  retryWithBackoff(() => api.post(`/AddMenuExclusionOption/${localId}/${optionId}`));

export const addMenuExclusionCategory = (localId: number, categoryId: number) =>
  retryWithBackoff(() => api.post(`/AddMenuExclusionCategory/${localId}/${categoryId}`));

// —— Promotions —— //

export const createPromotion = (localId: number, promo: PromotionPayload) =>
  retryWithBackoff(() => api.post(`/CreatePromotion/${localId}`, promo));

export const deletePromotion = (localId: number, promotionId: string) =>
  retryWithBackoff(() => api.delete(`/DeletePromotion/${localId}/${promotionId}`));

export const updatePromotion = (
  localId: number,
  promotionId: string,
  promo: PromotionPayload
) =>
  retryWithBackoff(() => api.put(
    `/UpdatePromotion/${localId}/${promotionId}`,
    promo
  ));

export const updateLocalDescription = (localId: number, description: string | null) =>
  retryWithBackoff(() => api.put(`/UpdateLocalDescription/${localId}`, { local_description: description }));


// /ToggleShowDescription/<int:local_id>

export const toggleShowDescription = (localId: number) =>
  retryWithBackoff(() => api.post(`/ToggleShowDescription/${localId}`));
